import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import QuickRegistration from '../components/customer/QuickRegistration';
import Text from '../components/shared/Text';
import { useToast } from '../components/shared/ToastNotification';

interface OrderRow {
    id: number;
    customerName: string;
    customerId: string;
    productName: string;
    productId: string;
    productBatches: any[];
    stockBatchId: string | null;
    quantity: number;
    unitPrice: string;
    discountPercent: number;
    tax_rate_id: string;
    tax_rate: number;
    total: number | string;
    showCustomerResults: boolean;
    showProductResults: boolean;
    isRegistering: boolean;
}

function QuickOrderEntry() {
    const navigate = useNavigate();
    const toast = useToast();
    const [rows, setRows] = useState<OrderRow[]>([createEmptyRow(0)]);
    const [loading, setLoading] = useState(false);

    // Search Caches
    const [customerCache, setCustomerCache] = useState<Record<number, any[]>>({});
    const [productCache, setProductCache] = useState<Record<number, any[]>>({});

    // Fetch initial data
    const customerSearchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const productSearchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    // Initial data fetch removed as we now use server-side search
    const [taxRates, setTaxRates] = useState<any[]>([]);
    
    // Initial data fetch removed as we now use server-side search
    useEffect(() => {
        fetchTaxRates();
    }, []);

    const fetchTaxRates = async () => {
        try {
            const response = await api.get('/tax_rates');
            setTaxRates(response.data || []);
        } catch (error) {
            console.error('Error fetching tax rates:', error);
        }
    };

    function createEmptyRow(index: number) {
        return {
            id: Date.now() + index,
            customerName: '',
            customerId: '',
            productName: '',
            productId: '',
            productBatches: [],
            stockBatchId: null as string | null,
            quantity: 1,
            unitPrice: '',
            discountPercent: 0,
            tax_rate_id: '',
            tax_rate: 0,
            total: 0,
            showCustomerResults: false,
            showProductResults: false,
            isRegistering: false,
        };
    }

    const addRow = () => {
        setRows(prev => [...prev, createEmptyRow(prev.length)]);
    };

    const removeRow = (index: number) => {
        if (rows.length === 1) {
            setRows([createEmptyRow(0)]);
        } else {
            setRows(prev => prev.filter((_, i) => i !== index));
        }
    };

    const updateRow = (index: number, updates: any) => {
        setRows(prev => prev.map((row, i) => {
            if (i !== index) return row;
            const updatedRow = { ...row, ...updates };

            // Auto-calculate total
            if (updates.quantity !== undefined || updates.unitPrice !== undefined || updates.tax_rate !== undefined || updates.discountPercent !== undefined) {
                const qty = parseFloat(updates.quantity !== undefined ? updates.quantity : row.quantity) || 0;
                const price = parseFloat(updates.unitPrice !== undefined ? updates.unitPrice : row.unitPrice) || 0;
                const disc = parseFloat(updates.discountPercent !== undefined ? updates.discountPercent : row.discountPercent) || 0;
                const tax = parseFloat(updates.tax_rate !== undefined ? updates.tax_rate : row.tax_rate) || 0;
                updatedRow.total = (qty * price * (1 - Math.min(disc, 100) / 100) * (1 + tax / 100)).toFixed(2);
            }

            return updatedRow;
        }));
    };

    const handleSearchCustomer = (query: string, index: number) => {
        if (!query) {
            updateRow(index, { showCustomerResults: false });
            return;
        }

        if (customerSearchTimeoutRef.current) {
            clearTimeout(customerSearchTimeoutRef.current);
        }

        customerSearchTimeoutRef.current = setTimeout(async () => {
            try {
                const response = await api.get('/search/customers', { q: query });
                const filtered = response.data?.customers || response.customers || [];
                setCustomerCache(prev => ({ ...prev, [index]: filtered }));
                updateRow(index, { showCustomerResults: true });
            } catch (e) {
                console.error("Customer search failed", e);
            }
        }, 300);
    };

    const handleSearchProduct = (query: string, index: number) => {
        if (!query) {
            updateRow(index, { showProductResults: false });
            return;
        }

        if (productSearchTimeoutRef.current) {
            clearTimeout(productSearchTimeoutRef.current);
        }

        productSearchTimeoutRef.current = setTimeout(async () => {
            try {
                const response = await api.get('/search/products', { q: query });
                const filtered = response.data?.products || response.products || [];
                setProductCache(prev => ({ ...prev, [index]: filtered }));
                updateRow(index, { showProductResults: true });
            } catch (e) {
                console.error("Product search failed", e);
            }
        }, 300);
    };

    const handleKeyDown = (e: React.KeyboardEvent, rowIndex: number, field: string) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            const fields = ['customerName', 'productName', 'quantity', 'unitPrice', 'discountPercent', 'gstRate'];
            const fieldIndex = fields.indexOf(field);

            if (fieldIndex < fields.length - 1) {
                const nextInput = document.querySelector(`input[data-row="${rowIndex}"][data-field="${fields[fieldIndex + 1]}"]`);
                if (nextInput) (nextInput as HTMLElement).focus();
            } else {
                if (rowIndex === rows.length - 1) {
                    addRow();
                    setTimeout(() => {
                        const nextRowInput = document.querySelector(`input[data-row="${rowIndex + 1}"][data-field="customerName"]`);
                        if (nextRowInput) (nextRowInput as HTMLElement).focus();
                    }, 50);
                } else {
                    const nextRowInput = document.querySelector(`input[data-row="${rowIndex + 1}"][data-field="customerName"]`);
                    if (nextRowInput) (nextRowInput as HTMLElement).focus();
                }
            }
        }
    };

    const saveOrders = async () => {
        const validRows = rows.filter(r => r.customerId && r.productId);
        if (validRows.length === 0) {
            toast.warning("Please add at least one valid order (Customer + Product)");
            return;
        }

        setLoading(true);
        try {
            let successCount = 0;
            const errors = [];

            for (const row of validRows) {
                const response = await api.post('/orders', {
                    order: {
                        customer_id: row.customerId,
                        status: 'confirmed',
                        order_date: new Date().toISOString().split('T')[0],
                        order_items_attributes: [
                            {
                                product_id: row.productId,
                                quantity: row.quantity,
                                unit_price: row.unitPrice,
                                tax_rate_id: row.tax_rate_id,
                                tax_rate: row.tax_rate,
                                discount_percent: row.discountPercent,
                                stock_batch_id: row.stockBatchId
                            }
                        ]
                    }
                });

                if (response.success) {
                    successCount++;
                } else {
                    const msg = response.errors?.length > 0 ? response.errors[0] : (response.message || response.error || "Failed to create order");
                    errors.push(`Row for ${row.customerName}: ${msg}`);
                }
            }

            if (errors.length > 0) {
                toast.error(`Created ${successCount} orders. ${errors.length} failed.`);
                // Keep the rows with errors
                console.error("Quick order errors:", errors);
            } else {
                toast.success(`Successfully created ${successCount} orders!`);
                navigate('/orders');
            }
        } catch (e: any) {
            console.error(e);
            toast.error(e.message || "An unexpected error occurred while saving.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="flex flex-col h-[calc(100vh-6rem)] bg-slate-50 rounded-xl shadow-lg border border-slate-200">
            {/* Header */}
            <div className="bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between shrink-0 z-20 shadow-sm">
                <div className="flex items-center gap-4">
                    <button onClick={() => navigate('/orders')} className="text-slate-400 hover:text-slate-600 transition-colors p-2 hover:bg-slate-50 rounded-lg">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                        </svg>
                    </button>
                    <div>
                        <Text variant="h3" color="primary">Quick Order Entry</Text>
                        <Text variant="small" color="slate">Create multiple orders rapidly</Text>
                    </div>
                </div>

                <div className="flex items-center gap-3">
                    <button onClick={() => navigate('/orders')} className="px-4 py-2 text-slate-600 bg-white border border-slate-300 hover:bg-slate-50 text-sm font-semibold rounded-lg shadow-sm transition-all">
                        Cancel
                    </button>
                    <button
                        onClick={saveOrders}
                        disabled={loading}
                        className="px-6 py-2 bg-emerald-600 text-white hover:bg-emerald-700 text-sm font-semibold rounded-lg shadow-md hover:shadow-lg transition-all flex items-center gap-2 disabled:opacity-50"
                    >
                        {loading ? 'Saving...' : 'Create Orders'}
                    </button>
                </div>
            </div>

            {/* Grid */}
            <div className="flex-1 overflow-auto bg-slate-50 p-6 relative w-full">
                <div className="min-w-[900px] bg-white rounded-xl shadow-sm border border-slate-200 overflow-visible">
                    <table className="w-full text-left border-collapse">
                        <thead className="bg-slate-100 sticky top-0 z-10">
                            <tr>
                                <th className="px-4 py-3 border-b border-slate-200 w-12 text-center"><Text variant="label" color="slate">#</Text></th>
                                <th className="px-4 py-3 border-b border-slate-200 min-w-[200px]"><Text variant="label" color="slate">Customer</Text></th>
                                <th className="px-4 py-3 border-b border-slate-200 min-w-[200px]"><Text variant="label" color="slate">Product</Text></th>
                                <th className="px-4 py-3 border-b border-slate-200 w-24 text-right"><Text variant="label" color="slate">Qty</Text></th>
                                <th className="px-4 py-3 border-b border-slate-200 w-32 text-right"><Text variant="label" color="slate">Price</Text></th>
                                <th className="px-4 py-3 border-b border-slate-200 w-24 text-right"><Text variant="label" color="slate">Disc %</Text></th>
                                <th className="px-4 py-3 border-b border-slate-200 w-24 text-right"><Text variant="label" color="slate">GST %</Text></th>
                                <th className="px-4 py-3 border-b border-slate-200 w-32 text-right"><Text variant="label" color="slate">Total</Text></th>
                                <th className="px-4 py-3 border-b border-slate-200 w-12"></th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {rows.map((row, index) => (
                                <tr key={row.id} className="group hover:bg-slate-50 transition-colors">
                                    <td className="px-4 py-3 text-center align-middle">
                                        <Text variant="mono" color="slate" className="text-xs">{index + 1}</Text>
                                    </td>
                                    <td className="px-2 py-2 p-0 align-top relative">
                                        <input
                                            type="text"
                                            data-row={index}
                                            data-field="customerName"
                                            className="w-full h-10 px-3 rounded-lg border-2 border-transparent bg-slate-50 focus:bg-white focus:border-emerald-500 focus:ring-4 focus:ring-emerald-500/10 outline-none transition-all text-sm font-medium"
                                            placeholder="Search Customer..."
                                            value={row.customerName}
                                            onChange={(e) => {
                                                updateRow(index, { customerName: e.target.value, customerId: '' });
                                                handleSearchCustomer(e.target.value, index);
                                            }}
                                            onKeyDown={(e) => handleKeyDown(e, index, 'customerName')}
                                            onFocus={() => row.customerName && handleSearchCustomer(row.customerName, index)}
                                            onBlur={() => setTimeout(() => updateRow(index, { showCustomerResults: false }), 200)}
                                            autoFocus={index === rows.length - 1 && rows.length > 1}
                                        />
                                        {row.isRegistering ? (
                                            <div className="absolute top-0 left-0 right-0 z-[60] p-1 bg-white shadow-2xl rounded-xl border border-indigo-200">
                                                <QuickRegistration
                                                    initialName={row.customerName}
                                                    onSuccess={(cust) => updateRow(index, { customerName: cust.name, customerId: cust.id, isRegistering: false, showCustomerResults: false })}
                                                    onCancel={() => updateRow(index, { isRegistering: false })}
                                                />
                                            </div>
                                        ) : row.showCustomerResults && customerCache[index] && (
                                            <div className="absolute top-full left-2 right-2 bg-white border border-slate-200 rounded-lg shadow-xl z-50 max-h-60 overflow-y-auto mt-1 divide-y divide-slate-100">
                                                {customerCache[index].length > 0 ? (
                                                    <>
                                                        {customerCache[index].map(cust => (
                                                            <div
                                                                key={cust.id}
                                                                onClick={() => updateRow(index, { customerName: cust.name, customerId: cust.id, showCustomerResults: false })}
                                                                className="px-4 py-3 hover:bg-emerald-50 cursor-pointer transition-colors"
                                                            >
                                                                <Text weight="semibold" color="primary">{cust.name}</Text>
                                                                <Text variant="small" color="slate">{cust.phone}</Text>
                                                            </div>
                                                        ))}
                                                        <div 
                                                            onClick={() => updateRow(index, { isRegistering: true, showCustomerResults: false })}
                                                            className="px-4 py-3 bg-indigo-50 hover:bg-indigo-100 cursor-pointer transition-colors border-t border-indigo-100 flex items-center gap-2"
                                                        >
                                                            <svg className="w-4 h-4 text-indigo-700" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" /></svg>
                                                            <Text variant="small" weight="bold" color="indigo" className="uppercase tracking-widest">Add New Customer</Text>
                                                        </div>
                                                    </>
                                                ) : (
                                                    <div className="p-4 text-center">
                                                        <Text variant="small" color="slate" className="mb-3 block">No customers found</Text>
                                                        <button
                                                            type="button"
                                                            onClick={() => updateRow(index, { isRegistering: true, showCustomerResults: false })}
                                                            className="w-full py-2 bg-slate-900 text-white rounded-lg text-[10px] font-bold uppercase tracking-widest hover:bg-black transition-all shadow-sm"
                                                        >
                                                            + Register "{row.customerName}"
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </td>
                                    <td className="px-2 py-2 p-0 align-top relative">
                                        <input
                                            type="text"
                                            data-row={index}
                                            data-field="productName"
                                            className="w-full h-10 px-3 rounded-lg border-2 border-transparent bg-slate-50 focus:bg-white focus:border-emerald-500 focus:ring-4 focus:ring-emerald-500/10 outline-none transition-all text-sm font-medium"
                                            placeholder="Search Product..."
                                            value={row.productName}
                                            onChange={(e) => {
                                                updateRow(index, { productName: e.target.value, productId: '' });
                                                handleSearchProduct(e.target.value, index);
                                            }}
                                            onKeyDown={(e) => handleKeyDown(e, index, 'productName')}
                                            onFocus={() => row.productName && handleSearchProduct(row.productName, index)}
                                            onBlur={() => setTimeout(() => updateRow(index, { showProductResults: false }), 200)}
                                        />
                                        {row.showProductResults && productCache[index] && (
                                            <div className="absolute top-full left-2 right-2 bg-white border border-slate-200 rounded-lg shadow-xl z-50 max-h-60 overflow-y-auto mt-1 divide-y divide-slate-100">
                                                {productCache[index].length > 0 ? productCache[index].map(prod => (
                                                    <div
                                                        key={prod.id}
                                                        onClick={async () => {
                                                            let batches: any[] = [];
                                                            try {
                                                                const detail = await api.get(`/products/${prod.id}`);
                                                                batches = (detail.data?.product?.batches || detail.data?.batches || []).filter((b: any) => (b.stock_quantity || 0) > 0);
                                                            } catch (_) { batches = prod.batches || []; }
                                                            const firstBatch = batches[0] || null;
                                                            updateRow(index, {
                                                                productName: prod.name,
                                                                productId: prod.id,
                                                                productBatches: batches,
                                                                stockBatchId: firstBatch?.id || null,
                                                                unitPrice: prod.selling_price || prod.price || prod.mrp || '',
                                                                tax_rate_id: firstBatch ? (firstBatch.tax_rate_id || prod.tax_rate_id || '') : (prod.tax_rate_id || ''),
                                                                tax_rate: firstBatch ? (firstBatch.tax_rate || 0) : (prod.gst_rate || 0),
                                                                showProductResults: false
                                                            });
                                                        }}
                                                        className="px-4 py-3 hover:bg-emerald-50 cursor-pointer transition-colors"
                                                    >
                                                        <div className="flex items-center gap-2">
                                                            <Text weight="semibold" color="primary">{prod.name}</Text>
                                                            {prod.requires_installation && (
                                                                <span className="px-1 py-0.5 bg-emerald-50 text-emerald-600 rounded text-[9px] font-black uppercase border border-emerald-100 shadow-sm animate-pulse">🔧 Install</span>
                                                            )}
                                                        </div>
                                                        <Text variant="small" weight="medium" className="text-emerald-600">Stock: {prod.stock_quantity || 0}</Text>
                                                    </div>
                                                )) : (
                                                    <div className="px-4 py-3">
                                                        <Text variant="small" color="slate">No products found</Text>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </td>
                                    <td className="px-2 py-2 p-0 align-top">
                                        <input
                                            type="number"
                                            data-row={index}
                                            data-field="quantity"
                                            className="w-full h-10 px-3 rounded-lg border-2 border-transparent bg-slate-50 focus:bg-white focus:border-emerald-500 focus:ring-4 focus:ring-emerald-500/10 outline-none transition-all text-sm text-right font-mono"
                                            value={row.quantity}
                                            onChange={(e) => updateRow(index, { quantity: e.target.value })}
                                            onKeyDown={(e) => handleKeyDown(e, index, 'quantity')}
                                        />
                                    </td>
                                    <td className="px-2 py-2 p-0 align-top">
                                        <input
                                            type="number"
                                            data-row={index}
                                            data-field="unitPrice"
                                            className="w-full h-10 px-3 rounded-lg border-2 border-transparent bg-slate-50 focus:bg-white focus:border-emerald-500 focus:ring-4 focus:ring-emerald-500/10 outline-none transition-all text-sm text-right font-mono"
                                            value={row.unitPrice}
                                            onChange={(e) => updateRow(index, { unitPrice: e.target.value })}
                                            onKeyDown={(e) => handleKeyDown(e, index, 'unitPrice')}
                                        />
                                    </td>
                                    <td className="px-2 py-2 p-0 align-top">
                                        <input
                                            type="number"
                                            data-row={index}
                                            data-field="discountPercent"
                                            className="w-full h-10 px-3 rounded-lg border-2 border-transparent bg-slate-50 focus:bg-white focus:border-emerald-500 focus:ring-4 focus:ring-emerald-500/10 outline-none transition-all text-sm text-right font-black text-indigo-900 font-mono"
                                            value={row.discountPercent}
                                            onChange={(e) => updateRow(index, { discountPercent: e.target.value })}
                                            onKeyDown={(e) => handleKeyDown(e, index, 'discountPercent')}
                                            placeholder="0"
                                        />
                                    </td>
                                    <td className="p-3 text-center">
                                        <select
                                            value={row.tax_rate_id}
                                            onChange={(e) => {
                                                const rateId = e.target.value;
                                                const rate = taxRates.find(r => String(r.id) === String(rateId));
                                                updateRow(index, { 
                                                    tax_rate_id: rateId,
                                                    tax_rate: rate ? parseFloat(rate.percentage) : 0
                                                });
                                            }}
                                            className="w-full px-2 py-2 text-[10px] font-black uppercase text-indigo-600 bg-white border border-slate-200 rounded-lg outline-none focus:border-indigo-500 appearance-none text-center"
                                        >
                                            <option value="">Select</option>
                                            {taxRates.map(tr => (
                                                <option key={tr.id} value={tr.id}>{tr.name}</option>
                                            ))}
                                        </select>
                                    </td>
                                    <td className="px-4 py-3 text-right align-middle">
                                        <Text variant="mono" weight="bold" color="slate">₹{row.total}</Text>
                                    </td>
                                    <td className="px-2 py-2 text-center align-middle">
                                        <button
                                            onClick={() => removeRow(index)}
                                            className="text-slate-300 hover:text-red-500 hover:bg-red-50 p-2 rounded-lg transition-all"
                                            title="Remove Row"
                                        >
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                            </svg>
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>

                    <button
                        onClick={addRow}
                        className="w-full p-4 border-t border-dashed border-slate-200 bg-slate-50 hover:bg-emerald-50 hover:border-emerald-200 cursor-pointer transition-all flex items-center justify-center gap-2 group"
                    >
                        <div className="h-6 w-6 rounded-full bg-slate-200 group-hover:bg-emerald-200 text-slate-500 group-hover:text-emerald-700 flex items-center justify-center transition-colors">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                            </svg>
                        </div>
                        <Text variant="body" weight="medium" color="slate" className="group-hover:text-emerald-600">Add Another Order</Text>
                    </button>
                </div>
            </div>
        </div>
    );
}

export default QuickOrderEntry;
